/**
 * Live STT 100% local: PCM worklet → VAD → Whisper chunks → Local Agreement.
 * Compatible con el shape de voiceLiveTranscript (Web Speech).
 */
import { PcmCaptureSession, isPcmCaptureSupported } from './pcmCapture'
import { VadSession } from './vadSession'
import { LocalAgreementBuffer } from './localAgreement'
import {
  canRunLocalWhisper,
  preloadWhisperModels,
  transcribeChunk,
  resolveLiveWhisperModel,
} from './transcribeLocal'
import { probeDeviceCapability } from './deviceCapability'

const FEATURE_FLAG_KEY = 'udv.voiceNotes.liveLocal'

/**
 * @returns {boolean}
 */
export function isLiveLocalEnabled() {
  try {
    if (typeof localStorage === 'undefined') return true
    const v = localStorage.getItem(FEATURE_FLAG_KEY)
    if (v == null || v === '') return true
    return v !== '0' && v !== 'false' && v !== 'off'
  } catch (e) {
    return true
  }
}

/**
 * @returns {boolean}
 */
export function canRunLiveLocalStt() {
  if (!isLiveLocalEnabled()) return false
  if (!isPcmCaptureSupported()) return false
  if (!canRunLocalWhisper()) return false
  try {
    const p = probeDeviceCapability()
    return !!(p && p.canLocalWhisper && (p.canLiveLocalStt !== false))
  } catch (e) {
    return false
  }
}

/**
 * Stub Gateway Edge (Fase D futura).
 * @returns {Promise<{ status: 'skipped', message: string }>}
 */
export async function transcribeChunkViaGateway() {
  return {
    status: 'skipped',
    message: 'Gateway Edge STT no implementado en esta versión',
  }
}

/**
 * Sesión de dictado local en vivo.
 */
export class LiveLocalSttSession {
  /**
   * @param {{
   *   onUpdate?: (snap: object) => void,
   * }} [opts]
   */
  constructor(opts = {}) {
    this.onUpdate = opts.onUpdate || null
    this._agreement = new LocalAgreementBuffer()
    /** @type {PcmCaptureSession|null} */
    this._pcm = null
    /** @type {VadSession|null} */
    this._vad = null
    this._paused = false
    this._running = false
    this._status = 'idle'
    this._vadState = 'idle'
    this._error = null
    this._inferring = 0
    this._model = resolveLiveWhisperModel()
  }

  getSnapshot() {
    const base = this._agreement.snapshot()
    return {
      ...base,
      status: this._status,
      vadState: this._vadState,
      error: this._error,
      engine: 'local_whisper',
    }
  }

  _emit() {
    if (!this.onUpdate) return
    try {
      this.onUpdate(this.getSnapshot())
    } catch (e) {
      /* ignore */
    }
  }

  _setStatus(status, error = null) {
    this._status = status
    this._error = error
    this._emit()
  }

  /**
   * @param {MediaStream} stream
   * @returns {Promise<boolean>}
   */
  async start(stream) {
    if (this._running) return true
    if (!canRunLiveLocalStt()) {
      this._setStatus(
        'error',
        'Dictado local no disponible en este dispositivo'
      )
      return false
    }
    if (!stream) {
      this._setStatus('error', 'Sin MediaStream')
      return false
    }

    this._agreement.reset()
    this._paused = false
    this._running = true
    this._inferring = 0
    this._model = resolveLiveWhisperModel()
    this._vadState = 'listening'
    this._setStatus('listening', null)

    try {
      this._vad = new VadSession({
        onSpeechStart: () => {
          if (this._paused) return
          this._vadState = 'speaking'
          this._status = 'speaking'
          this._emit()
        },
        onSpeechEnd: () => {
          if (this._paused) return
          this._vadState = this._inferring > 0 ? 'inferring' : 'listening'
          if (this._inferring === 0) this._status = 'listening'
          this._emit()
        },
        onSegment: (pcm, meta) => {
          if (this._paused || !this._running) return
          void this._handleSegment(pcm, meta)
        },
        onError: (err) => {
          this._setStatus('error', (err && err.message) || 'Error VAD')
        },
      })
      await this._vad.init()

      this._pcm = new PcmCaptureSession({
        onFrame: (pcm) => {
          if (this._paused || !this._vad) return
          this._vad.pushFrame(pcm)
        },
        onError: (err) => {
          this._setStatus('error', (err && err.message) || 'Error PCM')
        },
      })
      await this._pcm.start(stream)
      try {
        console.info('[UDV] live local STT listening', this._model)
      } catch (e) {
        /* ignore */
      }
      return true
    } catch (e) {
      await this.stop()
      this._setStatus(
        'error',
        (e && e.message) || 'No se pudo iniciar dictado local'
      )
      return false
    }
  }

  /**
   * @param {Float32Array} pcm
   * @param {{ sampleRate: number, reason: string }} meta
   */
  async _handleSegment(pcm, meta) {
    this._inferring += 1
    this._vadState = 'inferring'
    this._status = 'inferring'
    this._agreement.setDraft('…')
    this._emit()
    try {
      const result = await transcribeChunk(pcm, {
        sampleRate: (meta && meta.sampleRate) || 16000,
        modelId: this._model.modelId,
        modelTier: this._model.tier,
      })
      const text = result && result.text ? String(result.text).trim() : ''
      if (text) {
        this._agreement.pushConfirmed(text)
      } else {
        this._agreement.setDraft('')
      }
    } catch (e) {
      try {
        console.warn('[UDV] live chunk error', e)
      } catch (e2) {
        /* ignore */
      }
      this._agreement.setDraft('')
    } finally {
      this._inferring = Math.max(0, this._inferring - 1)
      if (this._inferring === 0 && this._running && !this._paused) {
        this._vadState =
          this._vadState === 'speaking' ? 'speaking' : 'listening'
        this._status =
          this._vadState === 'speaking' ? 'speaking' : 'listening'
      }
      this._emit()
    }
  }

  pause() {
    this._paused = true
    try {
      if (this._pcm) this._pcm.pause()
    } catch (e) {
      /* ignore */
    }
    try {
      if (this._vad) this._vad.flush()
    } catch (e) {
      /* ignore */
    }
    this._vadState = 'idle'
    this._setStatus('paused', null)
  }

  resume() {
    this._paused = false
    try {
      if (this._pcm) this._pcm.resume()
    } catch (e) {
      /* ignore */
    }
    this._vadState = 'listening'
    this._setStatus('listening', null)
  }

  /**
   * @returns {string} texto committed
   */
  async stop() {
    this._running = false
    this._paused = false
    try {
      if (this._vad) this._vad.flush()
    } catch (e) {
      /* ignore */
    }
    // Esperar inferencias en vuelo brevemente
    const deadline = Date.now() + 8000
    while (this._inferring > 0 && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 50))
    }
    try {
      if (this._pcm) await this._pcm.stop()
    } catch (e) {
      /* ignore */
    }
    try {
      if (this._vad) await this._vad.destroy()
    } catch (e) {
      /* ignore */
    }
    this._pcm = null
    this._vad = null
    this._vadState = 'idle'
    this._status = 'idle'
    const text = this._agreement.snapshot().finalText
    this._emit()
    return text
  }
}

/**
 * Precarga VAD + Whisper. Por defecto solo modelo live (evita saturar CPU/RAM del visor).
 * @param {{ onProgress?: (p: object) => void, mode?: 'live'|'final'|'all' }} [opts]
 * @returns {Promise<void>}
 */
export async function preloadLiveLocalStt(opts = {}) {
  if (!canRunLocalWhisper()) return
  const mode = opts.mode === 'final' || opts.mode === 'all' ? opts.mode : 'live'
  if (canRunLiveLocalStt() && (mode === 'live' || mode === 'all')) {
    try {
      const vad = new VadSession()
      await vad.init()
      await vad.destroy()
    } catch (e) {
      /* ignore */
    }
  }
  try {
    await preloadWhisperModels({ onProgress: opts.onProgress, mode })
  } catch (e) {
    try {
      console.warn('[UDV] whisper preload failed', e)
    } catch (e2) {
      /* ignore */
    }
  }
}

export default LiveLocalSttSession
